import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Button, Text, Spinner } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveProductUnits, deleteProductUnit } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

export default function ProductUnitDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const [id, setId] = useState(detail ? detail.id : ('productUnit-' + Date.now()))
  const [name, setName] = useState(detail ? detail.name : '')
  const [shortName, setShortName] = useState(detail ? detail.shortName : '')
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Emit detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[PRODUCT_UNIT_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        shortName: detail.shortName,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: detail ? () => (
        <HeaderIconButton
          onPress={async () => {
            // ATL-819 — `detailIndex` is a position in the `allData` snapshot this screen was
            // navigated with. Pairing it with a SEPARATELY recomputed `Object.values(productUnitsDict)`
            // (a live atom read, taken at press time) breaks that pairing the moment the dict changes
            // under the open screen, and splices out whichever unit now sits at that position. Every
            // sibling constants detail screen already writes through route `allData`; this one was the
            // outlier. The guard reads the same array, so it describes the array actually mutated.
            if (allData.length == 1) {
              alert(t('settings.onlyOneUnitError'))
              return
            }
            deleteProductUnit(context.db, detailIndex, allData)
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ) : null,
      title: (detail?.name || t('settings.newUnit'))
    })
  }, [])

  async function save() {
    setIsLoading(true)
    if (name === '' || !shortName === '') {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
      return
    }
    setSaveError('')
    let info = {
      name,
      shortName,
      id,
    }
    try {
      // ATL-819 — same pairing rule as the delete above: `detailIndex` addresses the `allData`
      // snapshot this screen was navigated with, so the SAVE must rewrite that same array. Reading
      // `Object.values(productUnitsDict)` here re-read the live atom at press time and applied a
      // snapshot index to a different array, overwriting whichever unit had shifted into that
      // position. It also mis-decided `isNew` (`index >= allData.length` against the live array),
      // so a concurrent insert turned a create into an update of the wrong row.
      await saveProductUnits(context.db, info, detailIndex, allData)
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
        <Text category='label' style={styles.label}>
          {t('settings.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          placeholder={t('piece')}
          autoCapitalize='sentences'
        />
        <Text category='label' style={styles.label}>
          {t('settings.shortName')}
        </Text>
        <StyledTextInput 
          value={shortName}
          onChangeText={text => setShortName(text)}
          returnKeyType='done'
          placeholder={t('pcs')}
          autoCapitalize='none'
        />
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
});